
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import Hls from 'hls.js';

interface BloombergRadioContextType {
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  volume: number;
  setVolume: (volume: number) => void;
}

const BloombergRadioContext = createContext<BloombergRadioContextType | undefined>(undefined);

export const BloombergRadioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  // Initialize audio element once on mount
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    audio.volume = volume;

    // Clean up on unmount
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  const play = () => {
    if (!audioRef.current) return;
    
    const bloombergRadioUrl = "https://www.bloomberg.com/media-manifest/streams/us.m3u8";

    // Check if the browser supports HLS natively
    if (audioRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Native support exists - typically iOS Safari
      audioRef.current.src = bloombergRadioUrl;
      audioRef.current.play().catch(e => console.error("Playback error:", e));
    } else if (Hls.isSupported()) {
      // Use HLS.js for browsers that don't have native support
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(bloombergRadioUrl);
      hls.attachMedia(audioRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        audioRef.current?.play().catch(e => console.error("Playback error:", e));
      });
    } else {
      console.error('Neither native HLS support nor HLS.js is available');
      return;
    }

    audioRef.current.onplaying = () => {
      setIsPlaying(true);
    };

    audioRef.current.onerror = () => {
      setIsPlaying(false);
      console.error("Audio playback error");
    };
  };

  const pause = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
  };

  const toggle = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };

  const updateVolume = (newVolume: number) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  return (
    <BloombergRadioContext.Provider
      value={{
        isPlaying,
        play,
        pause,
        toggle,
        volume,
        setVolume: updateVolume
      }}
    >
      {children}
    </BloombergRadioContext.Provider>
  );
};

export const useBloombergRadio = (): BloombergRadioContextType => {
  const context = useContext(BloombergRadioContext);
  if (context === undefined) {
    throw new Error('useBloombergRadio must be used within a BloombergRadioProvider');
  }
  return context;
};
